/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * grimoire/v1/import_export.proto
 * OpenAPI spec version: version not set
 */
import { useMutation } from '@tanstack/react-query';
import type {
  MutationFunction,
  QueryClient,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';

import * as axios from 'axios';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

import type {
  AutomationImportExportServiceExportAutomationBody,
  AutomationImportExportServiceImportAutomationBody,
  AutomationImportExportServiceMoveAutomationBody,
  AutomationImportExportServiceValidateAutomationImportBody,
  RpcStatus,
  V1ExportAutomationResponse,
  V1ImportAutomationResponse,
  V1MoveAutomationResponse,
  V1SeedWorkspaceFromTemplateRequest,
  V1SeedWorkspaceFromTemplateResponse,
  V1ValidateAutomationImportResponse,
} from '../models';

/**
 * @summary Exports a specific revision of an automation as a ZIP bundle.
Uploads the bundle to file storage and returns a 1-hour presigned download URL.
The bundle contains all automation phases, connections metadata, trigger metadata,
and provenance information. SPy code files are encrypted at rest within the bundle.
Fails with FAILED_PRECONDITION if the encryption key is not configured.
 */
export const exportAutomation = (
  organization: string,
  workspace: string,
  automation: string,
  automationImportExportServiceExportAutomationBody: AutomationImportExportServiceExportAutomationBody,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<V1ExportAutomationResponse>> => {
  return axios.default.post(
    `/api/v1/organizations/${organization}/workspaces/${workspace}/automations/${automation}:export`,
    automationImportExportServiceExportAutomationBody,
    options,
  );
};

export const getExportAutomationMutationOptions = <
  TError = AxiosError<RpcStatus>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof exportAutomation>>,
    TError,
    {
      organization: string;
      workspace: string;
      automation: string;
      data: AutomationImportExportServiceExportAutomationBody;
    },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof exportAutomation>>,
  TError,
  {
    organization: string;
    workspace: string;
    automation: string;
    data: AutomationImportExportServiceExportAutomationBody;
  },
  TContext
> => {
  const mutationKey = ['exportAutomation'];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof exportAutomation>>,
    {
      organization: string;
      workspace: string;
      automation: string;
      data: AutomationImportExportServiceExportAutomationBody;
    }
  > = props => {
    const { organization, workspace, automation, data } = props ?? {};

    return exportAutomation(
      organization,
      workspace,
      automation,
      data,
      axiosOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type ExportAutomationMutationResult = NonNullable<
  Awaited<ReturnType<typeof exportAutomation>>
>;
export type ExportAutomationMutationBody =
  AutomationImportExportServiceExportAutomationBody;
export type ExportAutomationMutationError = AxiosError<RpcStatus>;

/**
 * @summary Exports a specific revision of an automation as a ZIP bundle.
Uploads the bundle to file storage and returns a 1-hour presigned download URL.
The bundle contains all automation phases, connections metadata, trigger metadata,
and provenance information. SPy code files are encrypted at rest within the bundle.
Fails with FAILED_PRECONDITION if the encryption key is not configured.
 */
export const useExportAutomation = <
  TError = AxiosError<RpcStatus>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof exportAutomation>>,
      TError,
      {
        organization: string;
        workspace: string;
        automation: string;
        data: AutomationImportExportServiceExportAutomationBody;
      },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof exportAutomation>>,
  TError,
  {
    organization: string;
    workspace: string;
    automation: string;
    data: AutomationImportExportServiceExportAutomationBody;
  },
  TContext
> => {
  const mutationOptions = getExportAutomationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Moves a specific revision of an automation directly into another workspace
(in the same organization or a different one) within the same environment.
Unlike export/import, this is a direct intra-platform copy and does not produce
or consume a ZIP bundle. The source automation is left intact; a new draft
automation is created in the target workspace. Connections and triggers are
matched automatically in the target workspace.
 */
export const moveAutomation = (
  organization: string,
  workspace: string,
  automation: string,
  automationImportExportServiceMoveAutomationBody: AutomationImportExportServiceMoveAutomationBody,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<V1MoveAutomationResponse>> => {
  return axios.default.post(
    `/api/v1/organizations/${organization}/workspaces/${workspace}/automations/${automation}:move`,
    automationImportExportServiceMoveAutomationBody,
    options,
  );
};

export const getMoveAutomationMutationOptions = <
  TError = AxiosError<RpcStatus>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof moveAutomation>>,
    TError,
    {
      organization: string;
      workspace: string;
      automation: string;
      data: AutomationImportExportServiceMoveAutomationBody;
    },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof moveAutomation>>,
  TError,
  {
    organization: string;
    workspace: string;
    automation: string;
    data: AutomationImportExportServiceMoveAutomationBody;
  },
  TContext
> => {
  const mutationKey = ['moveAutomation'];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof moveAutomation>>,
    {
      organization: string;
      workspace: string;
      automation: string;
      data: AutomationImportExportServiceMoveAutomationBody;
    }
  > = props => {
    const { organization, workspace, automation, data } = props ?? {};

    return moveAutomation(
      organization,
      workspace,
      automation,
      data,
      axiosOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type MoveAutomationMutationResult = NonNullable<
  Awaited<ReturnType<typeof moveAutomation>>
>;
export type MoveAutomationMutationBody =
  AutomationImportExportServiceMoveAutomationBody;
export type MoveAutomationMutationError = AxiosError<RpcStatus>;

/**
 * @summary Moves a specific revision of an automation directly into another workspace
(in the same organization or a different one) within the same environment.
Unlike export/import, this is a direct intra-platform copy and does not produce
or consume a ZIP bundle. The source automation is left intact; a new draft
automation is created in the target workspace. Connections and triggers are
matched automatically in the target workspace.
 */
export const useMoveAutomation = <
  TError = AxiosError<RpcStatus>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof moveAutomation>>,
      TError,
      {
        organization: string;
        workspace: string;
        automation: string;
        data: AutomationImportExportServiceMoveAutomationBody;
      },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof moveAutomation>>,
  TError,
  {
    organization: string;
    workspace: string;
    automation: string;
    data: AutomationImportExportServiceMoveAutomationBody;
  },
  TContext
> => {
  const mutationOptions = getMoveAutomationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Validates an automation that may have been created via import. Reports whether
the automation was imported and, if so, what is still missing before it can run:
connections that have no match in the workspace and trigger kinds that are not
yet configured for the automation's pre-phases. Read-only; makes no changes.
 */
export const validateAutomationImport = (
  organization: string,
  workspace: string,
  automation: string,
  automationImportExportServiceValidateAutomationImportBody: AutomationImportExportServiceValidateAutomationImportBody,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<V1ValidateAutomationImportResponse>> => {
  return axios.default.post(
    `/api/v1/organizations/${organization}/workspaces/${workspace}/automations/${automation}:validateImport`,
    automationImportExportServiceValidateAutomationImportBody,
    options,
  );
};

export const getValidateAutomationImportMutationOptions = <
  TError = AxiosError<RpcStatus>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof validateAutomationImport>>,
    TError,
    {
      organization: string;
      workspace: string;
      automation: string;
      data: AutomationImportExportServiceValidateAutomationImportBody;
    },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof validateAutomationImport>>,
  TError,
  {
    organization: string;
    workspace: string;
    automation: string;
    data: AutomationImportExportServiceValidateAutomationImportBody;
  },
  TContext
> => {
  const mutationKey = ['validateAutomationImport'];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof validateAutomationImport>>,
    {
      organization: string;
      workspace: string;
      automation: string;
      data: AutomationImportExportServiceValidateAutomationImportBody;
    }
  > = props => {
    const { organization, workspace, automation, data } = props ?? {};

    return validateAutomationImport(
      organization,
      workspace,
      automation,
      data,
      axiosOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type ValidateAutomationImportMutationResult = NonNullable<
  Awaited<ReturnType<typeof validateAutomationImport>>
>;
export type ValidateAutomationImportMutationBody =
  AutomationImportExportServiceValidateAutomationImportBody;
export type ValidateAutomationImportMutationError = AxiosError<RpcStatus>;

/**
 * @summary Validates an automation that may have been created via import. Reports whether
the automation was imported and, if so, what is still missing before it can run:
connections that have no match in the workspace and trigger kinds that are not
yet configured for the automation's pre-phases. Read-only; makes no changes.
 */
export const useValidateAutomationImport = <
  TError = AxiosError<RpcStatus>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof validateAutomationImport>>,
      TError,
      {
        organization: string;
        workspace: string;
        automation: string;
        data: AutomationImportExportServiceValidateAutomationImportBody;
      },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof validateAutomationImport>>,
  TError,
  {
    organization: string;
    workspace: string;
    automation: string;
    data: AutomationImportExportServiceValidateAutomationImportBody;
  },
  TContext
> => {
  const mutationOptions = getValidateAutomationImportMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Imports an exported automation ZIP bundle into a workspace as a new draft 0.1.
Connections and triggers are re-wired via caller-supplied mappings.
If display_name conflicts with an existing automation in the target workspace,
the name is automatically resolved by prefixing with "1 - ", "2 - ", etc.
Fails with FAILED_PRECONDITION if the encryption key is not configured.
Fails with INVALID_ARGUMENT if the bundle is malformed or decryption fails.
 */
export const importAutomation = (
  organization: string,
  workspace: string,
  automationImportExportServiceImportAutomationBody: AutomationImportExportServiceImportAutomationBody,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<V1ImportAutomationResponse>> => {
  return axios.default.post(
    `/api/v1/organizations/${organization}/workspaces/${workspace}/automations:import`,
    automationImportExportServiceImportAutomationBody,
    options,
  );
};

export const getImportAutomationMutationOptions = <
  TError = AxiosError<RpcStatus>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof importAutomation>>,
    TError,
    {
      organization: string;
      workspace: string;
      data: AutomationImportExportServiceImportAutomationBody;
    },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof importAutomation>>,
  TError,
  {
    organization: string;
    workspace: string;
    data: AutomationImportExportServiceImportAutomationBody;
  },
  TContext
> => {
  const mutationKey = ['importAutomation'];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof importAutomation>>,
    {
      organization: string;
      workspace: string;
      data: AutomationImportExportServiceImportAutomationBody;
    }
  > = props => {
    const { organization, workspace, data } = props ?? {};

    return importAutomation(organization, workspace, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ImportAutomationMutationResult = NonNullable<
  Awaited<ReturnType<typeof importAutomation>>
>;
export type ImportAutomationMutationBody =
  AutomationImportExportServiceImportAutomationBody;
export type ImportAutomationMutationError = AxiosError<RpcStatus>;

/**
 * @summary Imports an exported automation ZIP bundle into a workspace as a new draft 0.1.
Connections and triggers are re-wired via caller-supplied mappings.
If display_name conflicts with an existing automation in the target workspace,
the name is automatically resolved by prefixing with "1 - ", "2 - ", etc.
Fails with FAILED_PRECONDITION if the encryption key is not configured.
Fails with INVALID_ARGUMENT if the bundle is malformed or decryption fails.
 */
export const useImportAutomation = <
  TError = AxiosError<RpcStatus>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof importAutomation>>,
      TError,
      {
        organization: string;
        workspace: string;
        data: AutomationImportExportServiceImportAutomationBody;
      },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof importAutomation>>,
  TError,
  {
    organization: string;
    workspace: string;
    data: AutomationImportExportServiceImportAutomationBody;
  },
  TContext
> => {
  const mutationOptions = getImportAutomationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Seeds a newly-created workspace with copies of every automation from the
environment's configured template workspace, each as a new draft 0.1.
The template source (org + workspace) is server configuration, NOT a request
input -- intended for INTERNAL service callers (e.g. agora) during org onboarding.
gRPC-only: intentionally NOT exposed via HTTP/gateway.
Authorizes the caller's create access on the destination workspace via Rakshak.
Fails with FAILED_PRECONDITION if the template workspace is not configured.
 */
export const seedWorkspaceFromTemplate = (
  v1SeedWorkspaceFromTemplateRequest: V1SeedWorkspaceFromTemplateRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<V1SeedWorkspaceFromTemplateResponse>> => {
  return axios.default.post(
    `/grimoire.v1.AutomationImportExportService/SeedWorkspaceFromTemplate`,
    v1SeedWorkspaceFromTemplateRequest,
    options,
  );
};

export const getSeedWorkspaceFromTemplateMutationOptions = <
  TError = AxiosError<RpcStatus>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof seedWorkspaceFromTemplate>>,
    TError,
    { data: V1SeedWorkspaceFromTemplateRequest },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof seedWorkspaceFromTemplate>>,
  TError,
  { data: V1SeedWorkspaceFromTemplateRequest },
  TContext
> => {
  const mutationKey = ['seedWorkspaceFromTemplate'];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof seedWorkspaceFromTemplate>>,
    { data: V1SeedWorkspaceFromTemplateRequest }
  > = props => {
    const { data } = props ?? {};

    return seedWorkspaceFromTemplate(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SeedWorkspaceFromTemplateMutationResult = NonNullable<
  Awaited<ReturnType<typeof seedWorkspaceFromTemplate>>
>;
export type SeedWorkspaceFromTemplateMutationBody =
  V1SeedWorkspaceFromTemplateRequest;
export type SeedWorkspaceFromTemplateMutationError = AxiosError<RpcStatus>;

/**
 * @summary Seeds a newly-created workspace with copies of every automation from the
environment's configured template workspace, each as a new draft 0.1.
The template source (org + workspace) is server configuration, NOT a request
input -- intended for INTERNAL service callers (e.g. agora) during org onboarding.
gRPC-only: intentionally NOT exposed via HTTP/gateway.
Authorizes the caller's create access on the destination workspace via Rakshak.
Fails with FAILED_PRECONDITION if the template workspace is not configured.
 */
export const useSeedWorkspaceFromTemplate = <
  TError = AxiosError<RpcStatus>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof seedWorkspaceFromTemplate>>,
      TError,
      { data: V1SeedWorkspaceFromTemplateRequest },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof seedWorkspaceFromTemplate>>,
  TError,
  { data: V1SeedWorkspaceFromTemplateRequest },
  TContext
> => {
  const mutationOptions = getSeedWorkspaceFromTemplateMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
